import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ExpiredLicenseModal from 'components/@common/Modals/ExpiredLicenseModal';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { useSelector } from 'react-redux';
import { getLastProductUrl } from '../util';
import { selectWorkbook } from 'state/modules/app';
import { getWorkbook } from 'util/workbook-util';
import { getLicenseIsExpired } from 'state/modules/user/selectors/user-selectors';

const getHomePath = ({ books, user }) => {
  const firstBook = books.filter(workbook => workbook.type === 'workbook')[0];
  const defaultRoot = `/home/${firstBook?.bookCode}/${firstBook?.edition}`;

  try {
    const url = getLastProductUrl({ books, user }) ?? defaultRoot;

    return url;
  } catch (err) {
    return defaultRoot;
  }
};

const useAppInit = () => {
  const [, { openModal }] = useModal();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const books = useSelector(({ entity }: any) => entity.workbooks);
  const user = useSelector(({ user }: any) => user);
  const hasExpiredLicense = useSelector(state => getLicenseIsExpired(state));
  const allBooks = useSelector(({ entity }: any) => entity.workbooks);
  const currentWorkbook: any = useSelector(({ app }: any) => app.currentWorkbook);
  const workbooks = allBooks.filter(book => book.type === 'workbook');
  const [loading, setLoading] = useState(true);
  const isEXTLink = ['ext', 'EXT'].some(x => location.pathname.includes(x));

  const workbookFromPath = getWorkbook(workbooks, location.pathname);

  const hasWorkbookAndExpiredLicense = !!currentWorkbook && hasExpiredLicense;

  const isExpirationModalSuppressed = !!user.settings?.suppressedExpirationAlerts?.find(
    ({ productCurriculumId }) =>
      productCurriculumId === currentWorkbook?.products?.[0]?.productCurriculumId
  );
  // app init: set workbook if none
  useEffect(() => {
    if (workbookFromPath && workbookFromPath.id !== currentWorkbook?.id) {
      dispatch(selectWorkbook(workbookFromPath) as any);
    } else if (user.licenses?.length) {
      const activeLicensesProdCurriculumIds = user.licenses
        .filter(l => l.expired)
        .map(x => x.productCurriculumId);

      if (!activeLicensesProdCurriculumIds.length) return;

      const foundBook = workbooks.find(workbook =>
        workbook?.products?.find(p =>
          activeLicensesProdCurriculumIds.includes(p.productCurriculumId)
        )
      );
      if (foundBook) dispatch(selectWorkbook(foundBook) as any);
    } else if (!currentWorkbook) {
      const firstBook = workbooks.filter(workbook => workbook.type === 'workbook')[0];
      if (firstBook) dispatch(selectWorkbook(firstBook) as any);
    }
  }, [workbookFromPath, workbooks, dispatch, currentWorkbook, user.licenses?.length]);

  // app init: override route, if necessary on user load
  useEffect(() => {
    if (!user || !currentWorkbook) return;
    setLoading(false);

    const shouldShowExpirationModal =
      (user.isForbidden ||
        (user.licenses && !user.licenses?.length) ||
        hasWorkbookAndExpiredLicense) &&
      !isExpirationModalSuppressed &&
      !user.isAuditor &&
      !isEXTLink;

    if (shouldShowExpirationModal) {
      openModal({ body: <ExpiredLicenseModal /> });
    }
  }, [
    user.isForbidden,
    user.isAuditor,
    user.licenses?.length,
    currentWorkbook?.id,
    hasWorkbookAndExpiredLicense
  ]);

  useEffect(() => {
    if (location.pathname === '/' && books.length) {
      setLoading(false);
      const url = getHomePath({ books, user });
      navigate(url, { replace: true });
    }
  }, [JSON.stringify(books), user]);

  return { loading };
};

export default useAppInit;
